import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import {
  BUTTON_RADIUS,
  COARSE_HIT_AREA,
  FOCUS_RING,
  cx,
} from "./controlStyles";

// The one button. Every colour is a theme token, so the same variant reads
// right in Glass, Earth and Midnight; geometry is fixed px (sm 28 / md 32 /
// lg 40) so toolbars line up in every theme.
//
//   primary      the one main action of a view (filled accent)
//   secondary    everyday actions (raised surface + hairline)
//   ghost        quiet actions in dense rows and toolbars
//   soft         accent-tinted, the old GlassButton "accent" look
//   danger       destructive, tinted (text in the danger colour)
//   danger-solid destructive confirmation (filled danger)
//
// Primary hover goes DARKER (accent-600) so on-accent text keeps >= 4.5:1.
// There is deliberately no default `type`: callers submit forms through
// it (ChatPane, TeamsPanel). GlassButton defaults to type="button".
//
// Hover styles only apply where a pointer really hovers (a tap would
// otherwise leave the hover look stuck on touch screens) and never on a
// disabled button; the pressed colour is repeated inside the same media
// block so it still shows on desktop (see CAN_HOVER in controlStyles).
// A disabled button keeps pointer events, so its `title` tooltip — often
// the only explanation of why it is disabled — still shows; the native
// `disabled` attribute already swallows the click.
//
// Disabled looks: the quiet variants fade to 45%. The FILLED ones
// (primary, danger-solid) become a neutral surface with subtle text
// instead — a faded white fill (Midnight) read brighter than an enabled
// secondary button, and a faded terracotta one (Earth) like a broken
// red button. A loading button (aria-busy) keeps its fill and spinner.
const FADE_WHEN_DISABLED = "disabled:opacity-45 aria-disabled:opacity-45";
const NEUTRAL_WHEN_DISABLED = [
  "[&:disabled:not([aria-busy])]:bg-raised [&:disabled:not([aria-busy])]:text-fg-subtle",
  "[&:disabled:not([aria-busy])]:shadow-[inset_0_0_0_1px_var(--c-border-subtle)]",
  "aria-disabled:bg-raised aria-disabled:text-fg-subtle aria-disabled:shadow-[inset_0_0_0_1px_var(--c-border-subtle)]",
].join(" ");

export const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center whitespace-nowrap font-medium select-none",
    BUTTON_RADIUS,
    "transition-[background-color,color,box-shadow,transform] duration-100 active:scale-[0.98]",
    FOCUS_RING,
    "disabled:cursor-not-allowed disabled:active:scale-100",
    // A link-button (GlassButton as="link") has no native disabled state.
    "aria-disabled:pointer-events-none",
    COARSE_HIT_AREA,
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-accent-500 text-on-accent active:bg-accent-700 [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-accent-600 [@media(hover:hover)]:[&:not(:disabled)]:active:bg-accent-700",
          NEUTRAL_WHEN_DISABLED,
        ].join(" "),
        secondary: [
          "bg-[color:var(--c-glass-button)] text-fg shadow-[inset_0_0_0_1px_var(--c-control-edge)] [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-[color:var(--c-glass-button-hover)] [@media(hover:hover)]:[&:not(:disabled)]:hover:shadow-[inset_0_0_0_1px_var(--c-text-faint)]",
          FADE_WHEN_DISABLED,
        ].join(" "),
        ghost: [
          "bg-transparent text-fg-muted [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-raised [@media(hover:hover)]:[&:not(:disabled)]:hover:text-fg",
          FADE_WHEN_DISABLED,
        ].join(" "),
        // accent-500/15 rather than bg-selected: Midnight's accent is
        // white, and its selected surface is too close to secondary's.
        soft: [
          "bg-accent-500/15 text-accent-fg shadow-[inset_0_0_0_1px_var(--c-glass-active-accent-border)] [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-accent-500/25",
          FADE_WHEN_DISABLED,
        ].join(" "),
        danger: [
          "bg-danger/10 text-danger shadow-[inset_0_0_0_1px_rgb(var(--tw-danger)/0.32)] [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-danger/[0.16]",
          FADE_WHEN_DISABLED,
        ].join(" "),
        "danger-solid": [
          "bg-danger-fill text-on-danger active:bg-danger-fill/80 [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-danger-fill/90 [@media(hover:hover)]:[&:not(:disabled)]:active:bg-danger-fill/80",
          NEUTRAL_WHEN_DISABLED,
        ].join(" "),
      },
      size: {
        sm: "h-[28px] gap-1.5 px-2.5 text-xs",
        md: "h-[32px] gap-2 px-3 text-sm",
        lg: "h-[40px] gap-2 px-4 text-base",
        icon: "h-[32px] w-[32px] p-0",
        "icon-sm": "h-[28px] w-[28px] p-0",
        "icon-lg": "h-[40px] w-[40px] p-0",
      },
      shape: {
        default: "",
        pill: "rounded-pill",
      },
      pressed: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // A toggle that is on reads as on in every theme: a clear accent
      // fill (25%) with an accent-light edge, not a slightly lighter
      // secondary - and not just an edge, which in Midnight looked like
      // the keyboard focus ring.
      {
        variant: ["secondary", "ghost"],
        pressed: true,
        className:
          "bg-accent-500/25 text-accent-fg shadow-[inset_0_0_0_1px_var(--c-accent-light)] [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-accent-500/30 [@media(hover:hover)]:[&:not(:disabled)]:hover:text-accent-fg [@media(hover:hover)]:[&:not(:disabled)]:hover:shadow-[inset_0_0_0_1px_var(--c-accent-light)]",
      },
      {
        variant: "soft",
        pressed: true,
        className:
          "bg-accent-500/25 shadow-[inset_0_0_0_1px_var(--c-accent-light)] [@media(hover:hover)]:[&:not(:disabled)]:hover:shadow-[inset_0_0_0_1px_var(--c-accent-light)]",
      },
      { variant: "primary", pressed: true, className: "bg-accent-600" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      shape: "default",
      pressed: false,
    },
  },
);

type LegacyVariant = "default" | "outline" | "destructive";
const legacyVariantMap: Record<LegacyVariant, "primary" | "secondary" | "danger"> = {
  default: "primary",
  outline: "secondary",
  destructive: "danger",
};

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;
export type ButtonShape = "default" | "pill";

export interface ButtonStyleOptions {
  variant?: ButtonVariant | LegacyVariant | null;
  size?: ButtonSize | null;
  shape?: ButtonShape | null;
  pressed?: boolean;
  className?: string;
}

/** The Button class string, for elements that must look like a button (links, labels). */
export function buttonClasses({ variant, size, shape, pressed, className }: ButtonStyleOptions = {}): string {
  const resolved =
    variant && variant in legacyVariantMap
      ? legacyVariantMap[variant as LegacyVariant]
      : (variant as ButtonVariant | null | undefined);
  return cx(buttonVariants({ variant: resolved, size, shape, pressed: !!pressed }), className);
}

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  variant?: ButtonVariant | LegacyVariant | null;
  size?: ButtonSize | null;
  /** "pill" = fully rounded ends (round icon buttons, chips). */
  shape?: ButtonShape;
  /** Shows a spinner, sets aria-busy and disables the button. */
  loading?: boolean;
  /** Toggle state: sets aria-pressed and holds the hover surface. */
  pressed?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, loading, pressed, disabled, children, ...props }, ref) => {
    const iconOnly = size === "icon" || size === "icon-sm" || size === "icon-lg";
    return (
      <button
        ref={ref}
        className={buttonClasses({ variant, size, shape, pressed, className })}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-pressed={pressed}
        {...props}
      >
        {loading && (
          <Loader2 aria-hidden className="h-4 w-4 shrink-0 animate-spin" strokeWidth={2} />
        )}
        {loading && iconOnly ? null : children}
      </button>
    );
  },
);
Button.displayName = "Button";
